import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface FileProcessorResult {
  text: string;
  metadata: {
    fileName: string;
    fileSize: number;
    fileType: string;
    pageCount?: number;
    processingTime: number;
  };
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export class FileProcessor {
  static async processFile(file: File): Promise<FileProcessorResult> {
    const startTime = Date.now();

    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size exceeds 10MB limit. Please upload a smaller file.');
    }

    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    let text: string;
    let pageCount: number | undefined;

    switch (fileExtension) {
      case '.pdf':
        ({ text, pageCount } = await this.processPDF(file));
        break;
      case '.docx':
        text = await this.processDOCX(file);
        break;
      case '.doc':
        text = await this.processDOC(file);
        break;
      case '.txt':
      case '.md':
        text = await this.processText(file);
        break;
      case '.rtf':
        text = await this.processRTF(file);
        break;
      default:
        throw new Error(`Unsupported file format: ${fileExtension}`);
    }

    const sanitizedText = this.sanitizeText(text);

    if (!sanitizedText.trim()) {
      throw new Error('The selected file appears to be empty or contains no extractable text.');
    }

    const processingTime = Date.now() - startTime;

    return {
      text: sanitizedText,
      metadata: {
        fileName: file.name,
        fileSize: file.size,
        fileType: fileExtension.substring(1),
        pageCount,
        processingTime,
      },
    };
  }

  private static async processPDF(file: File): Promise<{ text: string; pageCount: number }> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      const textParts: string[] = [];
      const pageCount = pdf.numPages;

      for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => {
            if ('str' in item) {
              return item.str;
            }
            return '';
          })
          .join(' ');

        textParts.push(pageText);
      }

      return {
        text: textParts.join('\n\n'),
        pageCount,
      };
    } catch (error) {
      console.error('PDF processing error:', error);
      throw new Error(
        'Failed to process PDF file. The file may be corrupted, password-protected, or contain unsupported content.'
      );
    }
  }

  private static async processDOCX(file: File): Promise<string> {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });

      if (result.messages.length > 0) {
        console.warn('DOCX processing warnings:', result.messages);
      }

      return result.value;
    } catch (error) {
      console.error('DOCX processing error:', error);
      throw new Error(
        'Failed to process DOCX file. The file may be corrupted or contain unsupported content.'
      );
    }
  }

  private static async processDOC(file: File): Promise<string> {
    throw new Error(
      'Legacy .doc files are not fully supported. Please convert to .docx format or save as .txt/.rtf and try again.'
    );
  }

  private static async processText(file: File): Promise<string> {
    try {
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          resolve(content);
        };
        reader.onerror = () => reject(new Error('Failed to read text file'));
        reader.readAsText(file);
      });
    } catch (error) {
      console.error('Text file processing error:', error);
      throw new Error('Failed to read text file. Please ensure the file is not corrupted.');
    }
  }

  private static async processRTF(file: File): Promise<string> {
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          resolve(content);
        };
        reader.onerror = () => reject(new Error('Failed to read RTF file'));
        reader.readAsText(file);
      });

      const plainText = this.stripRTFFormatting(text);
      return plainText;
    } catch (error) {
      console.error('RTF processing error:', error);
      throw new Error('Failed to process RTF file. Please ensure the file is not corrupted.');
    }
  }

  private static stripRTFFormatting(rtfText: string): string {
    let text = rtfText;

    text = text.replace(/\\par\b/g, '\n');
    text = text.replace(/\\line\b/g, '\n');
    text = text.replace(/\\tab\b/g, '\t');

    text = text.replace(/\{\\[^}]*\}/g, '');

    text = text.replace(/\\[a-z]+(-?\d+)?[ ]?/gi, '');

    text = text.replace(/[{}]/g, '');

    text = text.replace(/\n{3,}/g, '\n\n');

    return text.trim();
  }

  private static sanitizeText(text: string): string {
    return text
      .replace(/\0/g, '')
      .replace(/[\uFFFD\uFFFE\uFFFF]/g, '')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .trim();
  }

  static getSupportedFormats(): string[] {
    return ['.pdf', '.docx', '.txt', '.md', '.rtf'];
  }

  static getFormatDescription(): string {
    return 'PDF, DOCX, TXT, MD, RTF';
  }

  static getAcceptAttribute(): string {
    return '.pdf,.docx,.txt,.md,.rtf';
  }

  static validateFileType(file: File): { valid: boolean; error?: string } {
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    const supportedFormats = this.getSupportedFormats();

    if (!supportedFormats.includes(fileExtension)) {
      return {
        valid: false,
        error: `Unsupported file format: ${fileExtension}. Supported formats: ${this.getFormatDescription()}`,
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds 10MB limit. Your file is ${(file.size / (1024 * 1024)).toFixed(2)}MB.`,
      };
    }

    return { valid: true };
  }
}
